import React, { FC, useEffect, useState } from 'react';

import {
  ExceptionType,
  VulnerabilitySeverityStatus,
} from '@ariksa/compliance-policies/api';
import { NativeResources } from '@ariksa/inventory-core';
import {
  AggregatedAlertDetails,
  AlertResponse,
} from '@ariksa/notification/api';
import dayjs from 'dayjs';
import { each, isString, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import {
  renderSeverityInSelect,
  severityOptions,
} from 'components/DataDisplay/Utils/renderSeverity';
import { getIcon } from 'components/Icons/Components';
import { AddExceptionModal } from 'components/Overlay';
import { errorToast, successToast } from 'components/Toast';
import { ExceptionFor } from 'containers/Findings/FindingsOverview/types';
import { getBlueprintIcon } from 'containers/PolicyHub/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions } from 'containers/SharedState/slice';

interface IAddException {
  alert?: AlertResponse;
  aggregatedAlert?: AggregatedAlertDetails;
  exceptionFor: ExceptionFor;
  changeSeverity?: boolean;
  onClose();
  onSuccess?();
  isOpen: boolean;
}

export const AddException: FC<IAddException> = props => {
  const {
    alert,
    onClose,
    isOpen,
    aggregatedAlert,
    onSuccess,
    exceptionFor,
    changeSeverity = false,
  } = props;
  const { blueprints, addException } = useSelector(selectSharedState);
  const [exceptionDescription, setExceptionDescription] = useState('');
  const [exceptionApplyTime, setExceptionApplyTime] = useState({
    label: '6 months',
    value: dayjs().utc().add(6, 'months').format(),
  });
  const [selectedBlueprints, setSelectedBlueprints] = useState<
    Record<string, any>[]
  >([]);
  const [blueprintOptions, setBlueprintOptions] = useState<
    Record<string, any>[]
  >([]);
  const [exceptionType, setExceptionType] = useState<ExceptionType>(
    ExceptionType.Suppress,
  );
  const [newSeverity, setNewSeverity] = useState(
    alert?.severity
      ? {
          label: renderSeverityInSelect(alert?.severity?.toLowerCase()),
          value: alert?.severity?.toLowerCase(),
        }
      : severityOptions[0],
  );
  const dispatch = useDispatch();

  useEffect(() => {
    const allBlueprints = {};
    each(blueprints.data, o => {
      allBlueprints[o.id] = o;
    });
    let options = map(
      alert?.blueprints || aggregatedAlert?.blueprints,

      o => {
        const item = allBlueprints[o.blueprint_id];
        return {
          label: o.blueprint_name,
          value: o.blueprint_id,
          icon: getBlueprintIcon(
            item?.created_by === 'system',
            item?.compliance || item?.name,
          ),
        };
      },
    );

    if (!options?.length) {
      // Alert Vault aggregates carry blueprint_ids only (blueprints is []), so
      // fall back to the aggregate's ids too; tolerate ids not yet loaded.
      options = map(
        alert?.blueprint_ids ?? aggregatedAlert?.blueprint_ids,
        o => {
          const item = allBlueprints[o];
          return {
            label: item?.name ?? o,
            value: o,
            icon: getBlueprintIcon(
              item?.created_by === 'system',
              allBlueprints[o]?.compliance || getIcon(allBlueprints[o]?.name),
            ),
          };
        },
      );
    }

    setSelectedBlueprints(options);
    setBlueprintOptions(options);
  }, [alert, aggregatedAlert, blueprints.data]);

  useEffect(() => {
    if (changeSeverity) setExceptionType(ExceptionType.SeverityOverride);
  }, [changeSeverity]);

  const handleAddException = () => {
    dispatch(
      actions.addException({
        q: {
          blueprintExceptionCreate: {
            blueprint_ids: map(selectedBlueprints, o => o?.value),
            rule_ids: !!alert?.policy_id
              ? [alert?.policy_id]
              : aggregatedAlert?.policy_ids,
            native_resource: (alert?.resource ||
              (aggregatedAlert?.multi_resource
                ? 'CloudService'
                : aggregatedAlert?.resource_types?.[0])) as any,
            resource_ids: !!alert?.entity_uuid
              ? [alert?.entity_uuid]
              : undefined,
            // Finding-level exceptions have no single alert; take the
            // environment from the aggregate so the request is not rejected (422).
            environment_id: (alert?.environment_id ??
              aggregatedAlert?.environment_id)!,
            description: exceptionDescription,
            expire_at: exceptionApplyTime.value,
            exception_type: exceptionType,
            new_severity:
              exceptionType === ExceptionType.SeverityOverride
                ? (newSeverity.value?.toUpperCase() as VulnerabilitySeverityStatus)
                : undefined,
          },
        },
        onSuccess: res => {
          onSuccess?.();
          onClose();
          if (isString(res)) {
            successToast({ title: res });
          } else {
            successToast({
              title: `Successfully added exception!`,
            });
          }
        },
        onError: error =>
          errorToast({
            title: `Failed to add exception.`,
            description: error.description,
          }),
      }),
    );
  };

  return (
    <AddExceptionModal
      description={
        alert?.aggregate_message ?? aggregatedAlert?.description ?? undefined
      }
      resourceName={alert?.entity_name ?? undefined}
      resourceId={alert?.entity_id ?? undefined}
      onConfirm={() => handleAddException()}
      isOpen={isOpen}
      onClose={onClose}
      isMulti={!!aggregatedAlert}
      alert={alert}
      aggregatedAlert={aggregatedAlert}
      resourceType={
        (alert?.resource! as NativeResources) ||
        ((aggregatedAlert?.multi_resource
          ? 'CloudService'
          : aggregatedAlert?.resource_types?.[0]) as NativeResources)
      }
      blueprintOptions={blueprintOptions}
      selectedBlueprints={selectedBlueprints}
      setSelectedBlueprints={setSelectedBlueprints}
      environment={alert?.environment_id || aggregatedAlert?.environment_id!}
      accountId={alert?.account_id ?? aggregatedAlert?.account_id_list ?? []}
      exceptionDescription={exceptionDescription}
      setExceptionDescription={setExceptionDescription}
      setExceptionApplyTime={setExceptionApplyTime}
      exceptionApplyTime={exceptionApplyTime}
      exceptionFor={exceptionFor}
      exceptionType={exceptionType}
      setExceptionType={setExceptionType}
      newSeverity={newSeverity}
      setNewSeverity={setNewSeverity}
      currentSeverity={alert?.severity!}
      isLoading={addException.isLoading}
    />
  );
};
